/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = 2;
      this.page_size = this.dataset.pageSize;
      this.status = document.querySelector('[data-results-status]');
      this.error = this.querySelector('.pagination__error');
    }
    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    // Focus stays on the button, so a keyboard shopper continues from where the new products end; with the last
    // page the button goes, and focus moves to the first new product (renderProducts).
    loadMore() {
      if (!this.button) return;
      this.button.addEventListener('click', () => this.loadProducts());
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
      if (base.getAttribute('loading')) {
        return;
      }
      base.i++;
      base.showError(false);
      base.setBusy(true);
      base.showPlaceholders();
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((responseText) => {
          base.renderProducts(responseText, url);

          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        }, (error) => {
          // Only the request failed, nothing was added: clear the placeholders and let the same page be requested
          // again. An error while rendering is not caught here, so a page that was appended is never asked for twice.
          base.removePlaceholders();
          base.i--;
          base.setBusy(false);
          base.showError(true);
          console.error(error);
        });
    }
    // The wait and its end are announced through the grid's one status region (role=status, outside the grid).
    setBusy(busy) {
      if (busy) {
        this.setAttribute('loading', true);
      } else {
        this.removeAttribute('loading');
      }
      if (this.button) this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.grid) this.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
    // A failed request is shown under the button and announced; the next request clears it.
    showError(show) {
      if (!this.error) return;
      this.error.hidden = !show;
      if (show) this.announce(this.error.textContent.trim());
    }
    // The next rows fill with placeholder cards at once, as many as products still to come on this page.
    showPlaceholders() {
      const template = this.querySelector('template.pagination-placeholder');
      if (!template || !this.grid) return;
      const loaded = (this.i - 2) * this.page_size;
      const count = Math.max(0, Math.min(this.page_size, this.dataset.total - loaded));
      for (let n = 0; n < count; n++) {
        this.grid.appendChild(template.content.firstElementChild.cloneNode(true));
      }
    }
    removePlaceholders() {
      if (this.grid) this.grid.querySelectorAll(':scope > .product-card-placeholder').forEach((placeholder) => placeholder.remove());
    }
    renderProducts(html, url) {
      let base = this,
        grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');

      if (!grid_to_replace) {
        base.removePlaceholders();
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.setBusy(false);
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      let products = grid_to_replace.querySelectorAll('.column');
      // "24 of 50 products": the number now in the grid. The last page is the one that reaches the total, also when
      // the total is an exact multiple of the page size.
      const shown = Math.min(Number(this.dataset.total), (this.i - 1) * this.page_size);
      const last = shown >= Number(this.dataset.total) || products.length < this.page_size;
      const moveFocus = last && this.button && document.activeElement === this.button;

      const appendProducts = () => {
        base.removePlaceholders();
        products.forEach((product) => {
          this.grid.appendChild(product);
        });
        if (moveFocus) products[0]?.querySelector('a[href]')?.focus();
      };

      if (this.animations_enabled) {
        // GSAP staggers the new items in — a view transition on top
        // would snapshot them pre-animation and flicker. Append directly.
        appendProducts();
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.setBusy(false);
          }
        });
      } else {
        withViewTransition(appendProducts);
        base.setBusy(false);
        // The Stamped reviews app is optional: without it StampedFn is not declared at all.
        if (typeof StampedFn !== 'undefined' && typeof StampedFn.loadBadges === 'function') {
          StampedFn.loadBadges();
        }
      }
      if (last) {
        this.hide();
      }
      // The status template comes from the server with [shown] for the number now in the grid.
      if (this.dataset.status) this.announce(this.dataset.status.replace('[shown]', shown));
    }
    // The last page is in: Load more goes, infinite loading stops watching.
    hide() {
      if (this.button) this.button.style.display = 'none';
      if (this.observer) this.observer.unobserve(this);
    }
  }
  customElements.define('pagination-theme', Pagination);
}